import { useEffect, useState } from "react";
import { ISentinelRich } from "../../Models/ISentinel";
import "./SentinelTools.scss";
import { ISensor } from "../../Models/ISensor";
import {
  Label,
  MessageBar,
  MessageBarType,
  Pivot,
  PivotItem,
  PrimaryButton,
} from "@fluentui/react";
import { SummonModal } from "../SmartModal/SmartModal";
import { ISensorReading } from "../ConfigureCard/ConfigureCard";
import { isSensorOffline } from "../../Utils/SensorStatus";

export interface ISentinelToolsProps {
  sentinel: ISentinelRich | undefined;
  realTimeValues: ISensorReading[];
}

const BatteryDisplay = (props: { charge: number }) => {
  return (
    <div>
      {props.charge === 4 && (
        <img className="battery-icon" src="./assets/power_plug.svg" alt="Alimentato dal cavo" title="Alimentato dal cavo" />
      )}
      {props.charge === 3 && (
        <img className="battery-icon" src="./assets/battery_full.png" alt="Full battery" />
      )}
      {props.charge === 2 && (
        <img className="battery-icon" src="./assets/battery_mid.png" alt="Medium battery" />
      )}
      {props.charge === 1 && (
        <img className="battery-icon" src="./assets/battery_low.png" alt="Low battery" />
      )}
      {props.charge === 0 && (
        <img className="battery-icon" src="./assets/battery_dead.png" alt="Dead battery" />
      )}
    </div>
  );
};

const SentinelTools = (props: ISentinelToolsProps) => {
  const [refSentinel, setRefSentinel] = useState<ISentinelRich | undefined>();
  const [realTimeValues, setRealTimeValues] = useState<ISensorReading[]>([]);
  const [page, setPage] = useState<string>("");

  useEffect(() => {
    setRefSentinel(props.sentinel);
  }, [props.sentinel]);

  useEffect(() => {
    setRealTimeValues(props.realTimeValues);
  }, [props.realTimeValues]);

  const getRealTimeValue = (sensor: ISensor) => {
    let value: number = 0;
    let matches: ISensorReading[] = realTimeValues.filter(
      (x: ISensorReading) => x.sensorId == sensor.id
    );
    if (matches.length > 0) {
      value = +matches[0].lastValue;
    }
    value = value ?? 0;
    value = Math.floor(value);
    return value > 0 ? value : 0;
  };

  const hasAnyWeightSensor = () => {
    return (
      refSentinel?.sensors.find((x: ISensor) => x.type === "pesogr") !== undefined &&
      refSentinel?.sensors.find((x: ISensor) => x.type === "pesogr2") !== undefined
    );
  };

  const getWeightPatient = () => {
    let peso_1: ISensor | undefined = refSentinel?.sensors.find(
      (x: ISensor) => x.type === "pesogr"
    );
    let peso_2: ISensor | undefined = refSentinel?.sensors.find(
      (x: ISensor) => x.type === "pesogr2"
    );

    let tara_peso_1 = peso_1?.startThreshold ?? 0;
    let tara_peso_2 = peso_2?.startThreshold ?? 0;
    let taraTotale = tara_peso_1 + tara_peso_2;

    if (peso_1 && peso_2) {
      let real_time_p1 = getRealTimeValue(peso_1);
      let real_time_p2 = getRealTimeValue(peso_2);
      let finalWeight = (real_time_p1 + real_time_p2 - taraTotale) / 10;

      return {
        tara1: tara_peso_1,
        tara2: tara_peso_2,
        t1: peso_1.threshold,
        t2: peso_2.threshold,
        p1: real_time_p1 / 10,
        p2: real_time_p2 / 10,
        displayWeight: finalWeight,
      };
    }
    return null;
  };

  const weightInfo = getWeightPatient();

  const doorSensor = (refSentinel?.sensors ?? []).find(
    (x: ISensor) => x.type === "door"
  );

  // Componente interno per la porta - 100% funzionante e compilabile
    const DoorRealTimeCard = () => {
    if (!doorSensor) return null;

    const realTimeReading = realTimeValues.find(
      (r) => r.sensorId === doorSensor.id
    );

    // Usa il valore real-time se presente, altrimenti fallback al valore statico
    const rawValue = realTimeReading
      ? +realTimeReading.lastValue
      : +(doorSensor.lastValue ?? 0);

    // Batteria: la manda il subscriber in threshold (0..3) con la rotta keepalive; in lastValue
    // c'e' solo lo stato della porta. Leggerla da lastValue/10 dava sempre "scarica" (13/09).
    const rawBattery = realTimeReading?.threshold ?? doorSensor.threshold; // live se c'e', altrimenti statico
    const batteryLevel = rawBattery == null ? 3 : Math.min(4, Math.max(0, +rawBattery)); // 0-3 batteria, 4 = cavo

    // Stato porta (tollera vecchi valori combinati batteria*10+porta)
    const doorState = rawValue % 10; // 0 = chiusa, 1 = aperta

    const lastUpdate = realTimeReading?.updatedAt || doorSensor.updatedAt;
    const isLive = !!realTimeReading;

    return (
      <div className="door-sensor-info-card">
        <img src="assets/exit-room.png" alt="Porta" />

        <div>
          <div className="door-sensor-info-card-value">
            <BatteryDisplay charge={batteryLevel} />
          </div>
        </div>

        <div>
          <div className="door-sensor-info-card-label">Stato porta</div>
          <div className="door-sensor-info-card-value">
            {doorState === 1 ? "Aperta" : "Chiusa"}
          </div>
        </div>

        <div>
          <div className="door-sensor-info-card-label">Aggiornato il</div>
          <div className="door-sensor-info-card-value">
            <span>
              {new Date(lastUpdate).toLocaleDateString("it-IT")}
            </span>{" "}
            <span>
              {new Date(lastUpdate).toLocaleTimeString("it-IT")}
            </span>
            {isLive && (
              <span style={{ color: "#107c10", fontSize: "0.8em", marginLeft: "8px" }}>
                (live)
              </span>
            )}
          </div>
        </div>

        <div>
          <div className="door-sensor-info-card-label">Stato</div>
          <div className="door-sensor-info-card-value">
            {isSensorOffline(doorSensor) ? (
              <div className="door-sensor-offline">Offline</div>
            ) : (
              <div className="door-sensor-online">Online</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: "1em" }}>
      <Pivot aria-label="Basic Pivot Example">
        <PivotItem headerText="Bilancia">
          <div style={{ padding: "1em" }}>
            {!hasAnyWeightSensor() && (
              <MessageBar messageBarType={MessageBarType.warning}>
                Funzionalità bilancia non disponibile su questa Letto
              </MessageBar>
            )}
            {hasAnyWeightSensor() && (
              <div>
                <div className="auto-tara-row">
                  <div>
                    Ricordarsi di eseguire il calcolo della tara prima di
                    servire la persona assistita.
                  </div>
                  <PrimaryButton
                    iconProps={{ iconName: "Compare" }}
                    text="Calcola Tara"
                    onClick={() => {
                      SummonModal("configureCard-tara-modal");
                    }}
                  />
                </div>
                {weightInfo && (
                  <div className="monitoring-card-bilancia-wrap">
                    <div style={{ display: "flex", gap: "1em" }}>
                      <img src="assets/bilancia.png" />

                      <table>
                        <thead>
                          <tr>
                            <th>Dato</th>
                            <th>Valore letto</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Peso ospite (kg) *</td>
                            <td>
                              {weightInfo.displayWeight > 0
                                ? weightInfo.displayWeight.toFixed(1)
                                : "0.0"}
                            </td>
                          </tr>
                          <tr>
                            <td>Tara totale (kg) **</td>
                            <td>
                              {(weightInfo.tara1 + weightInfo.tara2) / 10}
                            </td>
                          </tr>
                          <tr>
                            <td>Sensore peso 1 (Kg) *</td>
                            <td>
                              {weightInfo.p1.toFixed(1)}
                              <br />
                              <small>
                                {"soglia allarme: "}
                                {weightInfo.t1 / 10}
                                {" kg"}
                              </small>
                            </td>
                          </tr>
                          <tr>
                            <td>Sensore peso 2 (Kg) *</td>
                            <td>
                              {weightInfo.p2.toFixed(1)}
                              <br />
                              <small>
                                {"soglia allarme: "}
                                {weightInfo.t2 / 10}
                                {" kg"}
                              </small>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <Label>
                        * Tutti i valori dei sensori peso sono Real-Time.
                      </Label>
                      <Label>
                        ** La tara corrisponde al peso percepito dai sensori
                        quando il letto è vuoto.
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </PivotItem>

        <PivotItem headerText="Attraversamento varchi">
          <div style={{ padding: "1em" }}>
            {!doorSensor && (
              <MessageBar messageBarType={MessageBarType.warning}>
                Funzionalità attraversamento varchi non disponibile per questa
                Letto.
              </MessageBar>
            )}
            {doorSensor && <DoorRealTimeCard />}
          </div>
        </PivotItem>
      </Pivot>

      <div></div>
    </div>
  );
};

export default SentinelTools;
